import { ReactElement, useMemo } from "react";
import { KnowledgeItemFormModel } from "./EditKnowledgeItemDialog";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import {
	ExistingHubItemImages,
	HubHierarchyListCategoryNodeModel,
	HubHierarchyListNodeModel
} from "client/Api/Hub/common.composer";
import { KnowledgeItemContent } from "client/Features/Hub/KnowledgeItems/Details/KnowledgeItemContent";
import {
	ensureNotNil,
	isNil,
	isNotNilOrWhiteSpace
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemNameOnlyCategory } from "client/Features/Hub/Content/common";
import { MediaContentModel } from "client/Features/Hub/HubDetailsLayout";
import {
	convertLayoutForDetails,
	convertMediaForDetails,
	convertTagForDetails
} from "client/Features/Hub/converters";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { ContentViewerEmptyBlockSettings, HubItemLayoutContentViewerModel } from "client/Features/Hub/Builder/Viewer/ContentViewer";
import { TagModel } from "client/Components";
import { LinkedDocumentPreviewModel } from "client/Features/Hub/LinkedDocumentsPreview";
import { getLinkedDocumentId } from "client/Features/Hub/useAddDocuments";
import { getDocumentKind } from "client_legacy/Documents/fileExtensions";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { useUserProfileStore } from "client/Features/Settings/UserProfile/userProfileStore";
import { ContentWithRelevant } from "client/Features/Hub/ContentWithRelevant";
import { tryFindHubCategoryItemNode } from "../../common";

export type PreviewProps = {
	model: KnowledgeItemFormModel;
	timezone: TimeZone;
	categoryHierarchy: readonly HubHierarchyListNodeModel[];
	originalImages: ExistingHubItemImages;
	createdBy: UserIdentityModel | null;
	publicationDate: Date | string | null;
};

export const Preview = (props: PreviewProps): ReactElement => {
	const {
		model,
		timezone,
		categoryHierarchy,
		originalImages,
		createdBy,
		publicationDate
	} = props;

	const now = new Date();

	const category = useMemo(
		(): HubItemCategoryModel | HubItemNameOnlyCategory => {
			if (isNil(model.categoryId)) {
				return {
					name: localize("_CategoryName")
				};
			}

			const tryFindCategoryResult: TryResult<HubHierarchyListCategoryNodeModel> = tryFindHubCategoryItemNode(
				categoryHierarchy,
				model.categoryId
			);

			if (!tryFindCategoryResult.wasSuccessful) {
				throw new Error(`Category data was not found category id: ${model.categoryId}`);
			}

			return tryFindCategoryResult
				.result
				.category;
		},
		[categoryHierarchy, model.categoryId]
	);

	const media = useMemo(
		(): MediaContentModel => {
			return convertMediaForDetails(
				model.media,
				HubItemKind.KnowledgeItem,
				originalImages.coverImage
			);
		},
		[model.media, originalImages.coverImage]
	);

	const layout = useMemo(
		(): HubItemLayoutContentViewerModel => {
			return convertLayoutForDetails(model.layout, originalImages.layoutImages);
		},
		[model.layout, originalImages.layoutImages]
	);

	const tags = useMemo(
		(): TagModel<number>[] => {
			return model.tags
				.map(convertTagForDetails);
		},
		[model.tags]
	);

	const linkedDocuments = useMemo(
		(): readonly LinkedDocumentPreviewModel[] => {
			return model.linkedDocuments
				.map(x => ({
					id: getLinkedDocumentId(x),
					name: x.name,
					thumbnailKind: getDocumentKind(
						DocumentItemType.File,
						x.extension
					)
				}));
		},
		[model.linkedDocuments]
	);

	const currentUserInfo: UserIdentityModel = ensureNotNil(
		useUserProfileStore(x => x.currentUserIdentity)
	);

	return (
		<ContentWithRelevant>
			<ContentWithRelevant.Content>
				<KnowledgeItemContent
					hasData
					title={isNotNilOrWhiteSpace(model.title)
						? model.title
						: {
							kind: "placeholder",
							value: localize("_EnterTitle")
						}
					}
					category={category}
					createdBy={model.showAuthor ? createdBy : null}
					isEditable={false}
					isPinned={model.isPinned}
					media={media}
					layout={layout}
					emptyBlockSettings={emptyBlockSettings}
					modifiedDate={now}
					modifiedBy={model.showAuthor ? currentUserInfo : null}
					publicationDate={publicationDate ?? now}
					tags={tags}
					linkedDocumentsMode="preview"
					linkedDocuments={linkedDocuments}
					hasStatistics={false}
					isInteractive={false}
					mode="details-only"
					timezone={timezone}
					shouldNotRenderEmptyFooter
					areCommentsEnabled={false}
				/>
			</ContentWithRelevant.Content>
		</ContentWithRelevant>
	);
};
Preview.displayName = "Preview";

const emptyBlockSettings: ContentViewerEmptyBlockSettings = {
	faqElement: {
		question: {
			kind: "placeholder",
			placeholder: localize("_EnterQuestion")
		},
		answer: {
			kind: "placeholder",
			placeholder: localize("_EnterAnswer")
		}
	}
};